/**
 * Parsea Excel/CSV de cámaras masivas.
 * Columnas reconocidas (case-insensitive):
 *   rtsp | url | url_rtsp | urlRtspOriginal | remote_camera_rtsp
 *   name | nombre | camara | camera
 *   recording_file_type | tipo | file_type
 * Si no hay encabezado y la celda empieza por rtsp://, se usa como RTSP.
 */
export interface MassiveCameraRow {
  name?: string;
  rtsp: string;
  recordingFileType?: string;
}

function normHeader(h: unknown): string {
  return String(h ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\s_-]+/g, '');
}

function pickRtsp(row: Record<string, unknown>, values: unknown[]): string | null {
  const keys = Object.keys(row);
  const rtspKey = keys.find((k) =>
    ['rtsp', 'url', 'urlrtsp', 'urlrtsporiginal', 'remotecamerartsp', 'stream', 'urlrtsp'].includes(
      normHeader(k)
    )
  );
  if (rtspKey) {
    const v = String(row[rtspKey] ?? '').trim();
    if (v.toLowerCase().startsWith('rtsp://')) return v;
  }
  for (const v of values) {
    const s = String(v ?? '').trim();
    if (s.toLowerCase().startsWith('rtsp://')) return s;
  }
  return null;
}

function pickName(row: Record<string, unknown>, index: number): string | undefined {
  const keys = Object.keys(row);
  const nameKey = keys.find((k) =>
    ['name', 'nombre', 'camara', 'camera', 'cam'].includes(normHeader(k))
  );
  if (nameKey) {
    const v = String(row[nameKey] ?? '').trim();
    if (v) return v;
  }
  return `Camara ${index + 1}`;
}

function pickFileType(row: Record<string, unknown>): string | undefined {
  const keys = Object.keys(row);
  const key = keys.find((k) =>
    ['recordingfiletype', 'tipo', 'filetype', 'formato'].includes(normHeader(k))
  );
  if (!key) return undefined;
  const v = String(row[key] ?? '').trim().toLowerCase();
  return v || undefined;
}

export function parseMassiveCameraSheetRows(rows: Record<string, unknown>[]): MassiveCameraRow[] {
  const out: MassiveCameraRow[] = [];
  const seen = new Set<string>();
  rows.forEach((row, index) => {
    const values = Object.values(row);
    const rtsp = pickRtsp(row, values);
    if (!rtsp) return;
    const key = rtsp.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      rtsp,
      name: pickName(row, index),
      recordingFileType: pickFileType(row),
    });
  });
  return out;
}

export async function parseMassiveCamerasExcel(file: File): Promise<MassiveCameraRow[]> {
  const name = (file.name || '').toLowerCase();
  if (name.endsWith('.csv') || name.endsWith('.tsv') || name.endsWith('.txt')) {
    const text = await file.text();
    return parseMassiveCamerasCsv(text, name.endsWith('.tsv') ? '\t' : undefined);
  }

  // Excel: requiere dependencia `xlsx`
  const XLSX = await import('xlsx');
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) return [];
  const sheet = wb.Sheets[sheetName];
  const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
  return parseMassiveCameraSheetRows(json);
}

function parseMassiveCamerasCsv(text: string, forcedSep?: string): MassiveCameraRow[] {
  const lines = text
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return [];

  const sep =
    forcedSep ||
    (lines[0].includes(';') && !lines[0].includes(',')
      ? ';'
      : lines[0].includes('\t')
        ? '\t'
        : ',');

  const split = (line: string) =>
    line.split(sep).map((c) => c.replace(/^"|"$/g, '').trim());

  const headerCells = split(lines[0]);
  const hasHeader = headerCells.some((h) => {
    const n = normHeader(h);
    return ['rtsp', 'url', 'nombre', 'name', 'camara'].includes(n) || n.includes('rtsp');
  });

  const rows: Record<string, unknown>[] = [];
  if (hasHeader) {
    for (let i = 1; i < lines.length; i++) {
      const cells = split(lines[i]);
      const row: Record<string, unknown> = {};
      headerCells.forEach((h, idx) => {
        row[h || `col${idx}`] = cells[idx] ?? '';
      });
      rows.push(row);
    }
  } else {
    for (const line of lines) {
      const cells = split(line);
      rows.push({ rtsp: cells[0], name: cells[1] || '' });
    }
  }
  return parseMassiveCameraSheetRows(rows);
}
